<script setup lang="ts">
import { computed, watch } from 'vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSubPanel from '@/components/design/composite/GaiaSubPanel.vue';
import {
  isEnumResponseType,
  type QuestionFormFields,
} from '@/utils/contractManagementView/columnQuestion';

/** The fields of an extraction question: prompt, response type, choice options, example data. */
const form = defineModel<QuestionFormFields>('form', { required: true });
/** The required prompt's message; cleared as soon as the prompt is typed into. */
const promptError = defineModel<string>('promptError', { default: '' });

const responseTypeOptions = [
  { label: 'Text', value: 'str' },
  { label: 'Integer', value: 'int' },
  { label: 'Float', value: 'float' },
  { label: 'Number', value: 'number' },
  { label: 'Yes/No', value: 'bool' },
  { label: 'Date', value: 'date' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'Choice (Single)', value: 'enum' },
  { label: 'Choice (Multiple)', value: 'multiple_enum' },
  { label: 'List of Text', value: 'list[str]' },
  { label: 'List of Integers', value: 'list[int]' },
  { label: 'List of Floats', value: 'list[float]' },
  { label: 'List of Numbers', value: 'list[number]' },
  { label: 'List of Yes/No', value: 'list[bool]' },
  { label: 'List of Dates', value: 'list[date]' },
  { label: 'List of Date & Time', value: 'list[datetime]' },
  { label: 'Currency Amount', value: 'CurrencyAmount' },
  { label: 'List of Currency Amounts', value: 'list[CurrencyAmount]' },
  { label: 'Entity', value: 'Entity' },
  { label: 'List of Entities', value: 'list[Entity]' },
  { label: 'Calendar Events', value: 'CalendarEvents' },
];

const isEnumType = computed(() => isEnumResponseType(form.value.responseType));

// A choice starts with the two options it needs.
watch(
  () => form.value.responseType,
  newType => {
    if (isEnumResponseType(newType) && form.value.enumOptions.length === 0) {
      form.value.enumOptions = [
        { name: '', definition: '', color: null },
        { name: '', definition: '', color: null },
      ];
    }
  }
);

function addEnumOption(): void {
  form.value.enumOptions.push({ name: '', definition: '', color: null });
}

function removeEnumOption(index: number): void {
  form.value.enumOptions.splice(index, 1);
}
</script>

<template>
  <gaia-form-field label="Prompt / question" required :error="promptError || undefined">
    <gaia-textarea
      v-model:value="form.prompt"
      :autosize="{ minRows: 4, maxRows: 10 }"
      placeholder="The question that extracts this data, e.g. “What is the total contract value?”"
      :maxlength="1000"
      show-count
      :status="promptError ? 'error' : undefined"
      style="width: 100%"
      @update:value="promptError = ''"
    />
  </gaia-form-field>

  <gaia-form-field label="Response type" required>
    <gaia-select
      v-model:value="form.responseType"
      :options="responseTypeOptions"
      placeholder="Select the expected data type"
      style="width: 100%"
    />
  </gaia-form-field>

  <!-- Choice types carry their own options; two is the minimum that makes
       a choice, which is why the last two rows can't be removed. -->
  <gaia-sub-panel v-if="isEnumType" label="Options">
    <template #actions>
      <gaia-add-button subject="option" size="xs" :icon="false" @click="addEnumOption" />
    </template>

    <n-flex vertical :size="10" :wrap="false">
      <!-- tone="plain" is the system's white-on-tinted pairing: the panel
           around these rows is already surfaceSoft, so a soft row on it is
           invisible. It also keeps the row's colours on the row's own
           element — a v-bind() here would resolve against this component's
           root, which is n-drawer's teleport, and never reach the row. -->
      <gaia-sub-panel v-for="(option, index) in form.enumOptions" :key="index" tone="plain">
        <n-flex align="flex-start" :size="8" :wrap="false">
          <n-flex vertical :size="8" :wrap="false" :style="{ flex: 1, minWidth: 0 }">
            <gaia-input
              v-model:value="option.name"
              placeholder="Option name"
              size="small"
              style="width: 100%"
            />
            <gaia-textarea
              v-model:value="option.definition"
              :autosize="{ minRows: 2 }"
              placeholder="Definition — tells the model when to pick this one"
              size="small"
              style="width: 100%"
            />
          </n-flex>
          <gaia-delete-button
            intent="remove"
            subject="option"
            :disabled="form.enumOptions.length <= 2"
            @click="removeEnumOption(index)"
          />
        </n-flex>
      </gaia-sub-panel>

      <gaia-text
        v-if="form.enumOptions.length === 0"
        size="control"
        tone="subtle"
        :style="{ textAlign: 'center', padding: '10px 0' }"
      >
        Add at least two options to define the choices.
      </gaia-text>
    </n-flex>
  </gaia-sub-panel>

  <gaia-form-field label="Example data" hint="Optional">
    <gaia-textarea
      v-model:value="form.exampleData"
      :autosize="{ minRows: 3, maxRows: 8 }"
      placeholder="Example answer in JSON"
      :maxlength="2000"
      style="width: 100%"
    />
  </gaia-form-field>
</template>
